import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useProjectStore } from '@/store/projectStore';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { VideoPlayer, VideoPlayerHandle } from '@/components/VideoPlayer';
import { CanvasOverlay } from '@/components/CanvasOverlay';
import { Toolbar } from '@/components/Toolbar';
import { Timeline } from '@/components/Timeline';
import { OverlayInspector } from '@/components/OverlayInspector';
import { ExportPanel } from '@/components/ExportPanel';
import { KeyboardShortcutsModal, useKeyboardShortcutsModal } from '@/components/KeyboardShortcutsModal';
import { AdminPage } from '@/pages/AdminPage';
import { SpeedInsights } from '@vercel/speed-insights/react';

function App() {
  // Route to admin page if URL path is /admin
  if (window.location.pathname === '/admin') {
    return <AdminPage />;
  }

  const videoPlayerRef = useRef<VideoPlayerHandle>(null);
  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  const [videoContainerSize, setVideoContainerSize] = useState({ width: 0, height: 0 });
  const [videoReady, setVideoReady] = useState(false);
  
  const video = useProjectStore((state) => state.project.video);
  
  // Handle when video is loaded
  const handleVideoLoaded = useCallback((videoEl: HTMLVideoElement) => {
    videoElementRef.current = videoEl;
    setVideoReady(true);
    
    // Update container size when video loads
    updateVideoContainerSize();
  }, []);
  
  // Update video container size
  const updateVideoContainerSize = useCallback(() => {
    if (videoPlayerRef.current) {
      const rect = videoPlayerRef.current.getVideoContainerRect();
      if (rect) {
        setVideoContainerSize({ width: rect.width, height: rect.height });
      }
    }
  }, []);
  
  // Reset video ready state when video changes
  useEffect(() => {
    if (!video) {
      setVideoReady(false);
      videoElementRef.current = null;
      setVideoContainerSize({ width: 0, height: 0 });
    }
  }, [video]);
  
  // Enable keyboard shortcuts
  useKeyboardShortcuts();
  
  // Keyboard shortcuts modal
  const keyboardShortcutsModal = useKeyboardShortcutsModal();
  
  // Track video container size for canvas overlay
  useEffect(() => {
    updateVideoContainerSize();
    window.addEventListener('resize', updateVideoContainerSize);
    
    return () => {
      window.removeEventListener('resize', updateVideoContainerSize);
    };
  }, [updateVideoContainerSize, videoReady]);
  
  // Create canvas overlay component to pass to VideoPlayer
  const canvasOverlayElement = useMemo(() => {
    if (!video || videoContainerSize.width === 0) return null;
    
    return (
      <CanvasOverlay
        videoWidth={video.width}
        videoHeight={video.height}
        containerWidth={videoContainerSize.width}
        containerHeight={videoContainerSize.height}
        videoRef={videoElementRef}
      />
    );
  }, [video, videoContainerSize.width, videoContainerSize.height]);
  
  return (
    <div className="w-full h-screen flex flex-col bg-editor-bg overflow-hidden">
      {/* App header / Toolbar */}
      <Toolbar />
      
      {/* Main content area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Video preview and canvas */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Preview area */}
          <div className="flex-1 relative overflow-hidden p-4">
            {/* Video player with canvas overlay inside */}
            <div className="w-full h-full relative rounded-lg overflow-hidden">
              <VideoPlayer 
                ref={videoPlayerRef} 
                onVideoLoaded={handleVideoLoaded}
                canvasOverlay={canvasOverlayElement}
              />
            </div>
          </div>
          
          {/* Timeline */}
          <Timeline />
        </div>
        
        {/* Properties panel */}
        <OverlayInspector />
      </div>
      
      {/* Export dialog */}
      <ExportPanel />
      
      {/* Keyboard Shortcuts Help Modal */}
      <KeyboardShortcutsModal
        isOpen={keyboardShortcutsModal.isOpen}
        onClose={keyboardShortcutsModal.close}
      />

      {/* Vercel Speed Insights */}
      <SpeedInsights />
    </div>
  );
}

export default App;

